import { map, combineLatest } from 'rxjs';
import { context$ } from 'streams/context/context$';
import { DependencyWheelViewModel } from '@ardoq/dependency-wheel';

import { composeViewModel, EMPTY_DEPENDENCY_WHEEL } from './composeViewModel';
import { ViewIds } from '@ardoq/api-types';
import type { APIReferenceType } from '@ardoq/api-types';
import { action$, combineReducers, streamReducer } from '@ardoq/rxbeach';
import { modelUpdateNotification$ } from 'modelInterface/modelUpdateNotification$';
import { graphModel$ } from 'modelInterface/graphModel$';
import { ExtractStreamShape } from 'tabview/types';
import { HasViewInstanceId } from '@ardoq/graph';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { componentInterface } from '@ardoq/component-interface';
import { toIds } from '@ardoq/core';
import { referenceInterface } from '@ardoq/reference-interface';
import { i18nLocale$ } from 'streams/i18nLocale$';
import type { WithI18nLocale } from '@ardoq/i18n';

export const getViewModel$ = ({ viewInstanceId }: HasViewInstanceId) => {
  const reset$ = combineLatest([
    getWorkspaceModeViewSettings$(ViewIds.DEPWHEEL, viewInstanceId),
    context$,
    graphModel$,
    modelUpdateNotification$,
  ]);
  const resetReducer = (
    _: DependencyWheelViewModel,
    [viewSettings, context, graphModel]: ExtractStreamShape<typeof reset$>
  ): DependencyWheelViewModel => {
    const subtreeIds = context.componentId
      ? toIds(componentInterface.getSubtree(context.componentId))
      : [];
    const workspaceComponentIds = componentInterface
      .getComponentsByWorkspaceId(context.workspaceId)
      .map(c => c._id);
    const referenceTypeMap = new Map<string, APIReferenceType>(
      Array.from(graphModel.referenceMap.keys()).flatMap(id => {
        const modelType = referenceInterface.getModelType(id);
        return modelType
          ? ([[id, modelType]] as [string, APIReferenceType][])
          : [];
      })
    );
    return composeViewModel(
      context,
      viewSettings,
      graphModel.referenceMap,
      subtreeIds,
      workspaceComponentIds,
      referenceTypeMap
    );
  };

  const baseViewModel$ = action$.pipe(
    combineReducers<DependencyWheelViewModel>(EMPTY_DEPENDENCY_WHEEL, [
      streamReducer(reset$, resetReducer),
    ])
  );

  return combineLatest([baseViewModel$, i18nLocale$]).pipe(
    map(([viewModel, i18nLocale]) => ({ ...viewModel, i18nLocale }))
  );
};

export type DependencyWheelViewProps = DependencyWheelViewModel &
  WithI18nLocale;
